import { Card, CardContent} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
// import ModifySelectionZF from "@/modules/selection/ModifySelectionZF";
import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SelectionResults } from "./SelectionResults";
import { ResultsBilanDisplayV2 } from "./ResultsBilanDisplayV2";
import { ResultsBilanComparatifDisplay } from "./ResultsBilanComparatifDisplay";

export interface Selection {
  id: string;
  timeline: string;
  patientId: string;
  selection: string;
  isDefault: boolean;
}

export interface SessionResults {
  id: number;
  sessionDate: string;
  resultsBilan: string;
  resultsZF: string;
  sujet: string;
  fiable: boolean;
  visualFieldInput: string;
  screenUsed : string;
  removedRows: number[];
  removedColumns: number[];
  sessionType: "BILAN" | "REEDUCATION";
  accountUsed: "PATIENT" | "CLINIQUE";
  order: number;
}

export default function BilanResultsTabs() {
  const [sessionsResults, setSessionsResults] = useState<SessionResults[]>([]);
  const [sessionResultsA, setSessionResultsA] = useState<SessionResults>();
  const [sessionResultsB, setSessionResultsB] = useState<SessionResults>();

  const { patientId } = useParams<{ patientId: string }>();

  // Navigation functions for Bilan results
  const navigateBilanA = (direction: 'prev' | 'next') => {
    if (!sessionResultsA || sessionsResults.length === 0) return;
    
    const currentIndex = sessionsResults.findIndex(session => session.id === sessionResultsA.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sessionsResults.length - 1;
    } else {
      newIndex = currentIndex < sessionsResults.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSessionResultsA(sessionsResults[newIndex]);
  };

  const navigateBilanB = (direction: 'prev' | 'next') => {
    if (!sessionResultsB || sessionsResults.length === 0) return;
    
    const currentIndex = sessionsResults.findIndex(session => session.id === sessionResultsB.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sessionsResults.length - 1;
    } else {
      newIndex = currentIndex < sessionsResults.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSessionResultsB(sessionsResults[newIndex]);
  };

  useEffect(() => {
    axios
      .get(`/practitioner/sessionsresults/bilan/${patientId}`)
      .then((response) => {
        const data: SessionResults[] = response.data;

        // On trie du plus récent au plus ancien :
        const sortedData = data.sort(
          (a, b) =>
            new Date(b.sessionDate).getTime() -
            new Date(a.sessionDate).getTime()
        );

        setSessionsResults(sortedData);

        // Sélection automatique des deux derniers bilans
        if (sortedData.length > 0) {
          setSessionResultsA(sortedData[0]); // le plus récent
        }
        if (sortedData.length > 1) {
          setSessionResultsB(sortedData[1]); // le second plus récent
        }
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des données:", error);
      });
  }, [patientId]);

  return (
    <Card className="w-5/6 h-5/6 pt-6" id="bilan-results-card">
      <CardContent className="flex flex-row">
        {/* Colonne de gauche : on affiche la vue ModifySelectionBilan ou ZF */}
        <div className="w-1/2 flex flex-col">
          <div className="flex flex-row">
            <div className="w-[90%]">
              <div className="mb-2 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateBilanA('next')}
                  disabled={sessionsResults.length <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1">
                  <SelectionResults
                    setSessionResults={setSessionResultsA}
                    sessionsResults={sessionsResults}
                    currentSession={sessionResultsA}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateBilanA('prev')}
                  disabled={sessionsResults.length <= 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="mb-4">
                {sessionResultsA &&
                sessionResultsA.resultsBilan !== null &&
                sessionResultsA.resultsBilan !== undefined &&
                sessionResultsA.resultsBilan !== "" &&
                sessionResultsA.resultsBilan !== "null" ? (
                  <div 
                    id="bilan-a-screen" 
                    data-caption={`Bilan A — ${sessionResultsA?.sessionDate ? new Date(sessionResultsA.sessionDate).toLocaleDateString('fr-FR') : ''}`}
                  >
                    <ResultsBilanDisplayV2 resultData={sessionResultsA} />
                  </div>
                ) : (
                  "Veuillez sélectionner un résultat"
                )}
              </div>
            </div>
            <strong className="w-1/4 ml-2 text-center flex items-center">
              A
            </strong>
          </div>
          <div className="flex flex-row mt-2">
            <div className="w-[90%]">
              <div className="mb-2 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateBilanB('next')}
                  disabled={sessionsResults.length <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1">
                  <SelectionResults
                    setSessionResults={setSessionResultsB}
                    sessionsResults={sessionsResults}
                    currentSession={sessionResultsB}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateBilanB('prev')}
                  disabled={sessionsResults.length <= 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div>
                {sessionResultsB &&
                sessionResultsB.resultsBilan !== null &&
                sessionResultsB.resultsBilan !== undefined &&
                sessionResultsB.resultsBilan !== "" &&
                sessionResultsB.resultsBilan !== "null" ? (
                  <div 
                    id="bilan-b-screen" 
                    data-caption={`Bilan B — ${sessionResultsB?.sessionDate ? new Date(sessionResultsB.sessionDate).toLocaleDateString('fr-FR') : ''}`}
                  >
                    <ResultsBilanDisplayV2 resultData={sessionResultsB} />
                  </div>
                ) : (
                  "Veuillez sélectionner un résultat"
                )}
              </div>
            </div>
            <strong className="w-1/4 ml-2 text-center flex items-center">
              B
            </strong>
          </div>
        </div>
        <div className="w-1/2 flex flex-col justify-center">
          <div 
            id="bilan-comparison-screen" 
            data-caption={`Comparaison Bilans — ${sessionResultsA?.sessionDate ? new Date(sessionResultsA.sessionDate).toLocaleDateString('fr-FR') : ''} vs ${sessionResultsB?.sessionDate ? new Date(sessionResultsB.sessionDate).toLocaleDateString('fr-FR') : ''}`}
          >
            <ResultsBilanComparatifDisplay
              bilanData1={sessionResultsA || { resultsBilan: "", sessionDate: "", sujet: "", fiable: false, visualFieldInput: "", screenUsed: "", removedRows: [], removedColumns: [], id: 0, resultsZF: "", sessionType: "BILAN", accountUsed: "PATIENT", order: 0 }}
              bilanData2={sessionResultsB || { resultsBilan: "", sessionDate: "", sujet: "", fiable: false, visualFieldInput: "", screenUsed: "", removedRows: [], removedColumns: [], id: 0, resultsZF: "", sessionType: "BILAN", accountUsed: "PATIENT", order: 0 }}
            />

            <p>
              Évolution des résultats entre le bilan du{" "}
              <strong>
                {sessionResultsA?.sessionDate
                  ? new Date(sessionResultsA.sessionDate).toLocaleDateString(
                      "fr-FR"
                    )
                  : "Date inconnue"}{" "}
              </strong>{" "} (A)
              et du{" "}
              <strong>
                {sessionResultsB?.sessionDate
                  ? new Date(sessionResultsB.sessionDate).toLocaleDateString(
                      "fr-FR"
                    )
                  : "Date inconnue"}{" "}
              </strong>{" "} (B)
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
